// Shim de `firebase/auth` — reproduit l'interface Firebase Auth utilisée par
// l'app (signInWithEmailAndPassword, signOut, onAuthStateChanged, setPersistence,
// sendPasswordResetEmail, user.getIdTokenResult) MAIS branché sur notre modèle
// existant : utilisateurs dans `ep:users` (email + mot de passe) + auto-login SSO
// portail via /api/me. Aucune dépendance à Firebase/Google.
//
// Le "custom claim" appId renvoyé par getIdTokenResult() = l'id métier de
// l'utilisateur ; le claim admin = son rôle admin. C'est ce que lit AuthContext.

const API = "";
const LS_UID = "ep_auth_uid";

// Constantes de persistance (comparées par référence dans authService).
export const browserLocalPersistence = { type: "local" };
export const browserSessionPersistence = { type: "session" };

let _persistence = browserLocalPersistence;

// ── Instance auth unique ────────────────────────────────────────────────────
const auth = {
  currentUser: null,
  _listeners: new Set(),
};

function notify() {
  for (const cb of auth._listeners) {
    try { cb(auth.currentUser); } catch { /* ignore */ }
  }
}
function setCurrentUser(u) {
  auth.currentUser = u;
  notify();
}

function isAdminUser(u) {
  return !!(u && (u.isAdmin === true || u.admin === true || u.role === "admin" || u.role === "Admin"));
}

function makeUser(u) {
  const user = {
    uid: String(u.id),
    email: u.email || "",
    displayName: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.name || "",
    _raw: u,
    async getIdTokenResult() {
      return { claims: { appId: u.id, admin: isAdminUser(u) } };
    },
    async getIdToken() { return "ep-" + String(u.id); },
  };
  return user;
}

// ── Accès aux utilisateurs via notre API ───────────────────────────────────
async function fetchUsers() {
  try {
    const r = await fetch(`${API}/api/appdata/ep:users`, { credentials: "include" });
    if (!r.ok) return [];
    const b = await r.json();
    return (b && b.value) || [];
  } catch { return []; }
}
async function findUserById(id) {
  const users = await fetchUsers();
  return users.find((x) => String(x.id) === String(id)) || null;
}
async function findUserByEmail(email) {
  const em = String(email || "").toLowerCase().trim();
  const users = await fetchUsers();
  return users.find((x) => String(x.email || "").toLowerCase().trim() === em) || null;
}

// ── Persistance locale de la session ────────────────────────────────────────
function persistUid(id) {
  const store = _persistence === browserSessionPersistence ? window.sessionStorage : window.localStorage;
  try { store.setItem(LS_UID, String(id)); } catch { /* ignore */ }
  // Nettoyer l'autre store pour éviter les sessions fantômes
  try {
    const other = _persistence === browserSessionPersistence ? window.localStorage : window.sessionStorage;
    other.removeItem(LS_UID);
  } catch { /* ignore */ }
}
function readPersistedUid() {
  try { const s = window.sessionStorage.getItem(LS_UID); if (s) return s; } catch { /* ignore */ }
  try { const l = window.localStorage.getItem(LS_UID); if (l) return l; } catch { /* ignore */ }
  return null;
}
function clearPersistedUid() {
  try { window.sessionStorage.removeItem(LS_UID); } catch { /* ignore */ }
  try { window.localStorage.removeItem(LS_UID); } catch { /* ignore */ }
}

// ── Initialisation : restaure la session (storage) puis tente le SSO ────────
let _initPromise = null;
function init() {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    let user = null;
    const uid = readPersistedUid();
    if (uid) {
      const u = await findUserById(uid);
      if (u) user = makeUser(u);
    }
    if (!user) {
      try {
        const me = await fetch(`${API}/api/me`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null));
        if (me && me.authenticated && me.email) {
          const u = await findUserByEmail(me.email);
          if (u) { user = makeUser(u); persistUid(u.id); }
        }
      } catch { /* portail injoignable */ }
    }
    setCurrentUser(user);
  })();
  return _initPromise;
}

// ── API publique (signatures Firebase) ──────────────────────────────────────
export function getAuth() { return auth; }

export async function setPersistence(_auth, persistence) {
  _persistence = persistence || browserLocalPersistence;
  return Promise.resolve();
}

// Connexion : en PROD on passe exclusivement par le SSO du portail (auto-login via
// /api/me) — plus aucun mot de passe n'est stocké ni vérifié ici. Ce point d'entrée
// n'existe plus que comme commodité de DEV (connexion par email seul, sans portail),
// neutralisée dans le build de production.
export async function signInWithEmailAndPassword(_auth, email, _password) {
  if (process.env.NODE_ENV === "production") {
    const e = new Error("Connecte-toi via le portail Esprit Padel.");
    e.code = "auth/operation-not-allowed";
    throw e;
  }
  const em = String(email || "").toLowerCase().trim();
  const users = await fetchUsers();
  const u = users.find((x) => String(x.email || "").toLowerCase().trim() === em);
  if (!u) {
    const e = new Error("Compte introuvable pour cet email.");
    e.code = "auth/user-not-found";
    throw e;
  }
  const user = makeUser(u);
  persistUid(u.id);
  setCurrentUser(user);
  return { user };
}

export async function signOut() {
  clearPersistedUid();
  setCurrentUser(null);
  return Promise.resolve();
}

export function onAuthStateChanged(_auth, cb) {
  auth._listeners.add(cb);
  init().then(() => { try { cb(auth.currentUser); } catch { /* ignore */ } });
  return () => auth._listeners.delete(cb);
}

export async function sendPasswordResetEmail(_auth, _email) {
  const e = new Error("La réinitialisation par email n'est pas disponible sur cette instance. Contactez un administrateur pour réinitialiser votre mot de passe.");
  e.code = "auth/operation-not-allowed";
  throw e;
}

export { auth };
export default { getAuth, setPersistence, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, browserLocalPersistence, browserSessionPersistence };
